import { z } from 'zod';
import type {
  FlagSpec,
  Government,
  MarchMove,
  MatchConfigInput,
  Order,
  RealtimeAction,
  TurnMission,
  UnitCounts,
} from '@war-game/engine';
import type { ResourceBag } from '@war-game/game-data';
import { normalizePhone } from './phone.js';

// ─── Básicos ─────────────────────────────────────────────────────────────
export const IdSchema = z.string().min(1).max(64);
export const LocationIdSchema = z.string().regex(/^[a-z0-9-]{2,40}$/);
export const UnitCountsSchema: z.ZodType<UnitCounts> = z.record(
  z.string().max(40),
  z.number().int().min(0).max(100_000),
);
export const ResourceBagSchema: z.ZodType<ResourceBag> = z.record(
  z.string().max(20),
  z.number().int().min(0).max(10_000_000),
);
const HexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/);

export const ErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
  details: z.unknown().optional(),
});
export type ApiError = z.infer<typeof ErrorSchema>;

// ─── Conta ───────────────────────────────────────────────────────────────
export const UsernameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z0-9_]{3,20}$/, 'Usuário: 3 a 20 caracteres (a-z, 0-9, _).');
export const PasswordSchema = z.string().min(8, 'Senha: mínimo de 8 caracteres.').max(128);
/** Celular brasileiro, normalizado para E.164 (`+5511999990000`). */
export const PhoneSchema = z
  .string()
  .trim()
  .max(30)
  .transform((value, ctx) => {
    const phone = normalizePhone(value);
    if (!phone) {
      ctx.addIssue({
        code: 'custom',
        message: 'Celular inválido: informe DDD + número (ex.: 11 99999-0000).',
      });
      return z.NEVER;
    }
    return phone;
  });
/** Código de 6 dígitos enviado por SMS. */
export const SmsCodeSchema = z
  .string()
  .trim()
  .regex(/^\d{6}$/, 'Código: 6 dígitos.');

export const RegisterBody = z.object({
  phone: PhoneSchema,
  username: UsernameSchema,
  password: PasswordSchema,
});
export const LoginBody = z.object({
  /** Nome de usuário ou celular confirmado. */
  login: z.string().trim().min(3).max(64),
  password: z.string().min(1).max(128),
});
export const PhoneBody = z.object({ phone: PhoneSchema });
export const VerifyPhoneBody = z.object({ code: SmsCodeSchema });
export const ForgotPasswordBody = z.object({ phone: PhoneSchema });
export const ResetPasswordBody = z.object({
  phone: PhoneSchema,
  code: SmsCodeSchema,
  password: PasswordSchema,
});
export const ChangePasswordBody = z.object({ current: z.string().min(1).max(128), password: PasswordSchema });
export const SmsAlertsBody = z.object({ enabled: z.boolean() });

export const UserSchema = z.object({
  id: z.string(),
  username: z.string(),
  /** Celular confirmado (E.164). */
  phone: z.string().nullable(),
  /** Celular aguardando o código. */
  pendingPhone: z.string().nullable(),
  phoneVerified: z.boolean(),
  /** Aceita avisos por SMS (lembrete de turno, interino); null = ainda não respondeu. */
  smsAlerts: z.boolean().nullable(),
  role: z.enum(['player', 'admin']),
  createdAt: z.string(),
});
export type User = z.infer<typeof UserSchema>;

export const AuthResponse = z.object({ accessToken: z.string(), expiresIn: z.number(), user: UserSchema });
export type AuthResponse = z.infer<typeof AuthResponse>;

// ─── Partidas ────────────────────────────────────────────────────────────
export const VictoryModeSchema = z.enum([
  'domination',
  'capitals',
  'supremacy',
  'economic',
  'military',
  'nuclear-terror',
  'control',
]);

export const CreateMatchBody = z.object({
  name: z.string().trim().min(3).max(60),
  maxPlayers: z.number().int().min(4).max(12),
  turnsPerDay: z.union([z.literal(1), z.literal(2)]),
  timezone: z.string().min(3).max(60).default('America/Sao_Paulo'),
  /** Desenvolvimento/playtest: duração do turno em minutos (sobrepõe turnsPerDay). */
  turnMinutes: z.number().int().min(1).max(1440).optional(),
  victory: z.object({
    mode: VictoryModeSchema,
    turnLimit: z.number().int().min(10).max(365).default(90),
    sharedAlliance: z.boolean().default(false),
  }),
  luck: z.boolean().default(true),
  maxAllies: z.number().int().min(0).max(11).default(2),
  bots: z.object({
    fill: z.boolean().default(true),
    difficulty: z.enum(['easy', 'medium', 'hard']).default('medium'),
  }),
  inactiveToBot: z.boolean().default(false),
  realtimeSpeed: z.number().min(1).max(1000).default(1),
  /** Partida fechada: não aparece na lista; entra quem tem o código de convite. */
  private: z.boolean().default(false),
  /** Single player: só você contra bots; o turno vira quando você manda (sem relógio). */
  singlePlayer: z.boolean().default(false),
});
export type CreateMatchBody = z.infer<typeof CreateMatchBody>;
// Garante que a configuração continua compatível com o engine.
export const _configCheck: (b: CreateMatchBody) => MatchConfigInput = (b) => b;

export const MatchStatusSchema = z.enum(['LOBBY', 'RUNNING', 'PAUSED', 'FINISHED']);

export const MatchSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  status: MatchStatusSchema,
  phase: z.enum(['PLANNING', 'RESOLVING']),
  maxPlayers: z.number(),
  players: z.array(
    z.object({
      id: z.string(),
      username: z.string(),
      isBot: z.boolean(),
      government: z.string(),
      spec: z.lazy(() => GovernmentSchema),
    }),
  ),
  currentTurn: z.number(),
  nextTurnAt: z.string().nullable(),
  turnsPerDay: z.number(),
  turnMinutes: z.number(),
  victoryMode: VictoryModeSchema,
  createdBy: z.string().nullable(),
  joined: z.boolean(),
  myPlayerId: z.string().nullable(),
  winner: z.array(z.string()).nullable(),
  private: z.boolean(),
  /** Código de convite (só para quem está na partida). */
  inviteCode: z.string().nullable(),
  /** Single player: turno manual (botão "Próximo turno"). */
  singlePlayer: z.boolean(),
});
export type MatchSummary = z.infer<typeof MatchSummarySchema>;

// ─── Governo ─────────────────────────────────────────────────────────────
export const FlagSchema: z.ZodType<FlagSpec> = z.object({
  pattern: z.enum([
    'solid',
    'horizontal',
    'vertical',
    'tricolor-h',
    'tricolor-v',
    'diagonal',
    'cross',
    'nordic',
    'canton',
    'stars-stripes',
  ]),
  colors: z.tuple([HexColor, HexColor, HexColor]),
  emblem: z.enum(['none', 'star', 'circle', 'eagle', 'crescent', 'sun', 'anchor', 'gear', 'crown']),
  emblemColor: HexColor,
});

export const GovernmentSchema: z.ZodType<Government> = z.object({
  name: z.string().trim().min(3).max(40),
  type: z.string().trim().min(2).max(30),
  colors: z.object({ primary: HexColor, secondary: HexColor, tertiary: HexColor.optional() }),
  flag: FlagSchema,
});
export const GovernmentPatch = z.object({
  name: z.string().trim().min(3).max(40).optional(),
  type: z.string().trim().min(2).max(30).optional(),
  colors: z.object({ primary: HexColor, secondary: HexColor, tertiary: HexColor.optional() }).optional(),
  flag: FlagSchema.optional(),
});
export const JoinMatchBody = z.object({
  government: GovernmentSchema.optional(),
  /** Código de convite (obrigatório em partida fechada). */
  code: z.string().trim().max(12).optional(),
});
export const StartMatchBody = z.object({
  /** Completa as vagas com bots (padrão: a configuração da partida). */
  fillBots: z.boolean().optional(),
});
export const RenameTerritoryBody = z.object({ name: z.string().trim().min(2).max(32).nullable() });

// ─── Ordens e missões ────────────────────────────────────────────────────
export const MarchMoveSchema: z.ZodType<MarchMove> = z.object({
  to: LocationIdSchema,
  units: UnitCountsSchema,
  rail: z.boolean().optional(),
  fleetIds: z.array(z.string().min(1).max(60)).max(100).optional(),
});

export const OrderSchema: z.ZodType<Order> = z.discriminatedUnion('type', [
  z.object({
    location: LocationIdSchema,
    type: z.literal('march'),
    special: z.boolean().optional(),
    moves: z.array(MarchMoveSchema).min(1).max(12),
  }),
  z.object({ location: LocationIdSchema, type: z.literal('defend'), special: z.boolean().optional() }),
  z.object({
    location: LocationIdSchema,
    type: z.literal('support'),
    special: z.boolean().optional(),
    target: LocationIdSchema,
    side: IdSchema.optional(),
  }),
  z.object({
    location: LocationIdSchema,
    type: z.literal('raid'),
    special: z.boolean().optional(),
    target: LocationIdSchema,
    units: UnitCountsSchema.optional(),
  }),
  z.object({ location: LocationIdSchema, type: z.literal('consolidate'), special: z.boolean().optional() }),
]);

export const MissionBody: z.ZodType<Omit<TurnMission, 'id' | 'owner'>> = z.object({
  type: z.enum([
    'bombing',
    'air-support',
    'air-patrol',
    'naval-strike',
    'missile',
    'air-transport',
    'relocation',
  ]),
  base: LocationIdSchema,
  target: LocationIdSchema,
  units: UnitCountsSchema,
  targetFacility: z.string().max(40).optional(),
  nuclear: z.boolean().optional(),
  missile: z.string().max(40).optional(),
  cargoUnits: UnitCountsSchema.optional(),
  oneWay: z.boolean().optional(),
  vessel: z.string().min(1).max(60).optional(),
  fleetIds: z.array(z.string().min(1).max(60)).max(100).optional(),
});

// ─── Ações em tempo real ─────────────────────────────────────────────────
const RegionSchema = z.union([
  z.object({
    kind: z.literal('continent'),
    id: z.enum(['asia', 'europe', 'africa', 'north-america', 'south-america', 'oceania']),
  }),
  z.object({
    kind: z.literal('ocean'),
    id: z.enum(['atlantic', 'mediterranean', 'indian', 'pacific', 'arctic-southern']),
  }),
]);
const TradeSide = z.object({
  resources: ResourceBagSchema,
  money: z.number().int().min(0),
  fromTerritory: LocationIdSchema.nullable(),
});
const TradeWant = z.object({
  resources: ResourceBagSchema,
  money: z.number().int().min(0),
  toTerritory: LocationIdSchema.nullable(),
});

export const ConstructBody = z.object({ facilityId: z.string().min(2).max(40) });
export const ProduceBody = z.object({
  territoryId: LocationIdSchema,
  facilityId: z.string().min(2).max(40),
  unitId: z.string().min(2).max(40),
  quantity: z.number().int().min(1).max(100),
  carrier: LocationIdSchema.optional(),
});
export const ResearchBody = z.object({
  researchId: z.string().min(2).max(40),
  centerId: LocationIdSchema.optional(),
});
export const IntelMissionBody = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('agent'),
    mission: z.enum(['infiltrate', 'intercept', 'sabotage', 'foment', 'tech-theft']),
    from: LocationIdSchema,
    target: LocationIdSchema,
    facilityId: z.string().max(40).optional(),
    researchId: z.string().max(40).optional(),
  }),
  z.object({
    kind: z.literal('recon'),
    from: LocationIdSchema,
    target: LocationIdSchema,
    units: UnitCountsSchema,
  }),
]);
export const RouteBody = z.object({
  from: LocationIdSchema,
  to: LocationIdSchema,
  mode: z.enum(['road', 'rail', 'air', 'sea']),
  cargo: ResourceBagSchema.optional(),
  surplusAbove: ResourceBagSchema.nullable().optional(),
  recurring: z.boolean().optional(),
  carriers: UnitCountsSchema.optional(),
});
export const RoutePatch = z.object({
  cargo: ResourceBagSchema.optional(),
  surplusAbove: ResourceBagSchema.nullable().optional(),
  recurring: z.boolean().optional(),
  active: z.boolean().optional(),
});
export const ProposalBody = z.object({
  to: IdSchema,
  proposal: z.enum(['nonAggression', 'alliance', 'peace']),
});
export const WarBody = z.object({ to: IdSchema });
export const TradeOfferBody = z.object({ to: IdSchema, give: TradeSide, want: TradeWant });
export const CommuniqueBody = z.object({ text: z.string().trim().min(3).max(280) });
export const MessageBody = z.object({
  channel: z.string().regex(/^(global|alliance|dm:[A-Za-z0-9_-]+)$/),
  text: z.string().trim().min(1).max(500),
});

export const RealtimeActionSchema: z.ZodType<RealtimeAction> = z.discriminatedUnion('type', [
  z.object({ type: z.literal('heartbeat') }),
  z.object({ type: z.literal('set-order'), order: OrderSchema }),
  z.object({ type: z.literal('clear-order'), location: LocationIdSchema }),
  z.object({ type: z.literal('add-mission'), mission: MissionBody }),
  z.object({ type: z.literal('remove-mission'), missionId: IdSchema }),
  z.object({ type: z.literal('construct'), territoryId: LocationIdSchema, facilityId: z.string().max(40) }),
  z.object({
    type: z.literal('cancel-construction'),
    territoryId: LocationIdSchema,
    constructionId: IdSchema,
  }),
  z.object({
    type: z.literal('produce'),
    territoryId: LocationIdSchema,
    facilityId: z.string().max(40),
    unitId: z.string().max(40),
    quantity: z.number().int().min(1).max(100),
    carrier: LocationIdSchema.optional(),
  }),
  z.object({ type: z.literal('cancel-production'), itemId: IdSchema }),
  z.object({
    type: z.literal('research'),
    researchId: z.string().max(40),
    centerId: LocationIdSchema.optional(),
  }),
  z.object({
    type: z.literal('agent-mission'),
    mission: z.enum(['infiltrate', 'intercept', 'sabotage', 'foment', 'tech-theft']),
    from: LocationIdSchema,
    target: LocationIdSchema,
    facilityId: z.string().max(40).optional(),
    researchId: z.string().max(40).optional(),
    agents: z.number().int().min(1).max(20).optional(),
  }),
  z.object({
    type: z.literal('recon'),
    from: LocationIdSchema,
    target: LocationIdSchema,
    units: UnitCountsSchema,
  }),
  z.object({
    type: z.literal('station-agents'),
    from: LocationIdSchema,
    to: LocationIdSchema,
    count: z.number().int().min(1).max(1000),
  }),
  z.object({
    type: z.literal('market'),
    territoryId: LocationIdSchema,
    resource: z.string().max(20),
    quantity: z.number().int().min(1).max(100_000),
    side: z.enum(['buy', 'sell']),
  }),
  z.object({
    type: z.literal('market-buy'),
    lotId: z.string().min(1).max(20),
    territoryId: LocationIdSchema,
  }),
  z.object({
    type: z.literal('market-bid'),
    auctionId: z.string().min(1).max(20),
    territoryId: LocationIdSchema,
    amount: z.number().int().min(1).max(100_000_000),
  }),
  z.object({
    type: z.literal('name-unit'),
    unitRef: z.string().min(1).max(60),
    name: z.string().max(40).nullable(),
  }),
  z.object({
    type: z.literal('vessel-load'),
    vesselRef: z.string().min(1).max(60),
    from: LocationIdSchema,
    units: UnitCountsSchema,
  }),
  z.object({
    type: z.literal('vessel-unload'),
    vesselRef: z.string().min(1).max(60),
    to: LocationIdSchema,
    units: UnitCountsSchema,
  }),
  z.object({
    type: z.literal('route-create'),
    from: LocationIdSchema,
    to: LocationIdSchema,
    mode: z.enum(['road', 'rail', 'air', 'sea']),
    cargo: ResourceBagSchema.optional(),
    surplusAbove: ResourceBagSchema.nullable().optional(),
    recurring: z.boolean().optional(),
    carriers: UnitCountsSchema.optional(),
  }),
  z.object({
    type: z.literal('route-update'),
    routeId: IdSchema,
    cargo: ResourceBagSchema.optional(),
    surplusAbove: ResourceBagSchema.nullable().optional(),
    recurring: z.boolean().optional(),
    active: z.boolean().optional(),
  }),
  z.object({ type: z.literal('route-delete'), routeId: IdSchema }),
  z.object({ type: z.literal('declare-war'), to: IdSchema }),
  z.object({
    type: z.literal('propose'),
    to: IdSchema,
    proposal: z.enum(['nonAggression', 'alliance', 'peace']),
  }),
  z.object({ type: z.literal('respond-proposal'), proposalId: IdSchema, accept: z.boolean() }),
  z.object({ type: z.literal('cancel-proposal'), proposalId: IdSchema }),
  z.object({ type: z.literal('break-treaty'), with: IdSchema }),
  z.object({ type: z.literal('betray'), with: IdSchema }),
  z.object({ type: z.literal('trade-offer'), to: IdSchema, give: TradeSide, want: TradeWant }),
  z.object({
    type: z.literal('trade-respond'),
    offerId: IdSchema,
    accept: z.boolean(),
    toTerritory: LocationIdSchema.optional(),
    fromTerritory: LocationIdSchema.optional(),
  }),
  z.object({ type: z.literal('trade-cancel'), offerId: IdSchema }),
  z.object({ type: z.literal('set-government'), government: GovernmentPatch }),
  z.object({
    type: z.literal('rename-territory'),
    territoryId: LocationIdSchema,
    name: z.string().max(32).nullable(),
  }),
  z.object({
    type: z.literal('set-retreat'),
    territoryId: LocationIdSchema,
    to: LocationIdSchema.nullable(),
  }),
  z.object({ type: z.literal('communique'), text: z.string().trim().min(3).max(280) }),
  z.object({
    type: z.literal('launch-satellite'),
    territoryId: LocationIdSchema,
    unitId: z.string().max(40),
    region: RegionSchema.optional(),
    band: z.enum(['north', 'equator', 'south']).optional(),
  }),
  z.object({
    type: z.literal('asat'),
    from: LocationIdSchema,
    targetPlayer: IdSchema,
    satelliteId: IdSchema.optional(),
  }),
]) as z.ZodType<RealtimeAction>;

export const ActionResponse = z.object({ ok: z.literal(true), createdId: z.string().optional() });

// ─── Notícias, chat, placar ─────────────────────────────────────────────
export const NewsItemSchema = z.object({
  id: z.string(),
  turn: z.number(),
  level: z.enum(['headline', 'news', 'note']),
  region: z.string().nullable(),
  players: z.array(z.string()),
  text: z.string(),
  body: z.string().optional(),
  kind: z.string(),
});
export const NewsQuery = z.object({
  turn: z.coerce.number().int().min(1).optional(),
  level: z.enum(['headline', 'news', 'note']).optional(),
  region: z.string().max(30).optional(),
  player: z.string().max(64).optional(),
});

export const ChatMessageSchema = z.object({
  id: z.string(),
  channel: z.string(),
  from: z.object({ playerId: z.string(), username: z.string(), government: z.string() }),
  text: z.string(),
  createdAt: z.string(),
});
export type ChatMessage = z.infer<typeof ChatMessageSchema>;

/** Locais favoritos do jogador na partida (sobem ao topo das listas e seletores). */
export const MAX_FAVORITES = 30;
export const FavoritesSchema = z.object({ ids: z.array(z.string()).max(MAX_FAVORITES) });
export type Favorites = z.infer<typeof FavoritesSchema>;

// ─── Traçado da cidade no mapa do território (editor do admin) ───────────
/** Coordenadas na geometria pré-projetada do mapa (mesmas unidades do SVG). */
const MapCoord = z.number().finite().min(-10_000).max(10_000);
export const TownPointSchema = z.tuple([MapCoord, MapCoord]);
export const TownFacilitySchema = z.object({
  x: MapCoord,
  y: MapCoord,
  /** Rotação do prédio em graus (o rótulo fica sempre na horizontal). */
  rot: z.number().finite().min(-360).max(360),
  /** Escala do prédio sobre o tamanho do traçado (ausente = 1). */
  scale: z.number().finite().min(0.1).max(10).optional(),
});
export const TownRoadSchema = z.object({
  /** Polilinha (vértices da estrada, como uma trilha de PCB). */
  points: z.array(TownPointSchema).min(2).max(200),
  /** `main`: estrada principal (traço largo); `street`: rua. */
  kind: z.enum(['main', 'street']),
});
export const TownLayoutSchema = z.object({
  /** Largura de um prédio, em unidades do mapa. */
  size: z.number().finite().positive().max(1_000),
  facilities: z.record(z.string().regex(/^[a-z0-9-]{2,40}$/), TownFacilitySchema),
  roads: z.array(TownRoadSchema).max(500),
});
export type TownPoint = z.infer<typeof TownPointSchema>;
export type TownRoad = z.infer<typeof TownRoadSchema>;
export type TownLayout = z.infer<typeof TownLayoutSchema>;
/** Traçados salvos, por território. */
export const TownLayoutsSchema = z.record(z.string(), TownLayoutSchema);
export type TownLayouts = z.infer<typeof TownLayoutsSchema>;

export const ScoreEntrySchema = z.object({
  playerId: z.string(),
  score: z.number(),
  gdp: z.number(),
  military: z.number(),
  consolidated: z.number(),
  occupied: z.number(),
  seaZones: z.number(),
  research: z.number(),
  capitals: z.number(),
});

export const TimeResponse = z.object({ serverTime: z.string() });
